/*
 * ------------------------------------------------------------
 * Buzzwoo · Ideenverlag · Countdown-Zeitfenster (PDP + Quick-Add-Drawer)
 *
 * <div …Countdown-Block… hidden>            (snippets/standard-blocks.liquid)
 *   <bw-cd-window data-start="20:00" data-end="11:00" data-tz="Europe/Berlin"
 *                 data-workdays data-holidays data-closed="24.12., 31.12."></bw-cd-window>
 *   …
 * </div>
 *
 * Das leere Steuer-Element schaltet sein Elternelement (den Block) — kein
 * Wrapper, damit Ignites `> * + *`-Abstände in der Buybox unverändert bleiben.
 *
 * Zeigt den Countdown nur im Fenster [start, end) in der Shop-Zeitzone,
 * auch über Mitternacht (20:00 → 11:00). Mit data-workdays/-holidays/-closed
 * zusätzlich nur, wenn der Ziel-Tag (der Tag, an dem `end` erreicht wird)
 * ein Versandtag ist: Mo–Fr, kein Feiertag in Hamburg, kein eingetragener
 * Schließtag. Fr 20:00 → Sa 11:00 ist damit aus, So 20:00 → Mo 11:00 an.
 *
 * Muss im Browser laufen: Shopify cached die Seite, ein Liquid-`now` wäre
 * veraltet. Im Fenster stellt das Element den Ziel-Zeitpunkt des
 * <custom-countdown> auf das nächste `end` (das Liquid-`data-end-date` kann
 * aus dem Cache stammen). Prüft jede halbe Minute neu. Ohne JS bleibt der
 * Block ausgeblendet; im Theme-Editor (data-design-mode) immer sichtbar.
 * Settings: Countdown-Block › „BW: …“ (sections/main-product.liquid).
 * ------------------------------------------------------------
 */
if (!customElements.get('bw-cd-window')) {
  const toMinutes = (value) => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
    if (!match) return null;
    return (Number(match[1]) % 24) * 60 + Number(match[2]);
  };

  /** Datum + Sekunden seit Mitternacht in der Zeitzone `tz`. */
  const zonedNow = (tz) => {
    const now = new Date();
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }).formatToParts(now);
      const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
      return {
        y: get('year'), m: get('month'), d: get('day'),
        seconds: (get('hour') % 24) * 3600 + get('minute') * 60 + get('second'),
      };
    } catch (e) {
      return {
        y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate(),
        seconds: now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds(),
      };
    }
  };

  /** Kalendertag (UTC-Date, nur Datumsteil zählt) um `offset` Tage verschoben. */
  const calendarDay = (y, m, d, offset = 0) => new Date(Date.UTC(y, m - 1, d + offset));
  const dayKey = (date) => `${date.getUTCMonth() + 1}-${date.getUTCDate()}`;

  /** Ostersonntag (Gauß/Meeus) als UTC-Date. */
  const easter = (y) => {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(y, month - 1, day));
  };

  /** Gesetzliche Feiertage in Hamburg (bundesweit + Reformationstag). */
  const hamburgHolidays = (y) => {
    const fixed = ['1-1', '5-1', '10-3', '10-31', '12-25', '12-26'];
    const e = easter(y);
    const moving = [-2, 1, 39, 50].map((n) => dayKey(new Date(e.getTime() + n * 86400000)));
    return new Set([...fixed, ...moving]);
  };

  /** „24.12., 31.12.2026“ → Set von `M-D` bzw. `Y-M-D`. */
  const parseClosed = (value) => {
    const set = new Set();
    String(value || '').split(/[,;\n]+/).forEach((raw) => {
      const match = /^\s*(\d{1,2})\.(\d{1,2})\.?(\d{4})?\s*$/.exec(raw);
      if (!match) return;
      const key = `${Number(match[2])}-${Number(match[1])}`;
      set.add(match[3] ? `${match[3]}-${key}` : key);
    });
    return set;
  };

  class BwCdWindow extends HTMLElement {
    connectedCallback() {
      this.block = this.parentElement;
      if (!this.block) return;
      this.closed = parseClosed(this.dataset.closed);
      this.update();
      // Kinder (custom-countdown) sind beim Parsen evtl. noch nicht da
      requestAnimationFrame(() => this.update());
      this.timer = setInterval(() => this.update(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    isShippingDay(date) {
      const weekday = date.getUTCDay();
      if (this.hasAttribute('data-workdays') && (weekday === 0 || weekday === 6)) return false;
      const key = dayKey(date);
      if (this.hasAttribute('data-holidays') && hamburgHolidays(date.getUTCFullYear()).has(key)) return false;
      if (this.closed.has(key) || this.closed.has(`${date.getUTCFullYear()}-${key}`)) return false;
      return true;
    }

    update() {
      if (this.hasAttribute('data-design-mode')) {
        this.block.hidden = false;
        return;
      }
      const start = toMinutes(this.dataset.start);
      const end = toMinutes(this.dataset.end);
      if (start === null || end === null || start === end) {
        this.block.hidden = false;
        return;
      }
      const now = zonedNow(this.dataset.tz || 'Europe/Berlin');
      const s = start * 60;
      const e = end * 60;
      const inside = s < e ? now.seconds >= s && now.seconds < e : now.seconds >= s || now.seconds < e;
      // Ziel-Tag: über Mitternacht gezählt endet das Fenster am Folgetag
      const target = calendarDay(now.y, now.m, now.d, s > e && now.seconds >= s ? 1 : 0);
      if (!inside || !this.isShippingDay(target)) {
        this.block.hidden = true;
        return;
      }
      const untilEnd = (e - now.seconds + 86400) % 86400;
      this.retarget(Date.now() + untilEnd * 1000);
      this.block.hidden = false;
    }

    retarget(target) {
      const countdown = this.block.querySelector('custom-countdown');
      if (!countdown) return;
      const iso = new Date(Math.floor(target / 1000) * 1000).toISOString();
      if (countdown.dataset.endDate === iso) return;
      countdown.dataset.endDate = iso;
      // Schon gestartet (eager oder sichtbar geworden)? Dann neu anwerfen.
      if (countdown.intervalTimer !== undefined && typeof countdown.init === 'function') {
        clearInterval(countdown.intervalTimer);
        countdown.init();
      }
    }
  }

  customElements.define('bw-cd-window', BwCdWindow);
}
